/** Parse debit/credit from API (number or localized string). */
export const parseEntryLineAmount = (value) => {
  if (value == null || value === '') return 0;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const raw = String(value).trim().replace(/\s/g, '');
  if (!raw) return 0;
  if (/^\d{1,3}(\.\d{3})+$/.test(raw)) {
    return parseInt(raw.replace(/\./g, ''), 10) || 0;
  }
  const normalized = raw.includes(',') && !raw.includes('.')
    ? raw.replace(/\./g, '').replace(',', '.')
    : raw.replace(/,/g, '');
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
};

export const isIngresoAccountKey = (accountKey) => /^4(\.|$)/.test(String(accountKey || '').trim());

/** Signed net on a line: debit minus credit. */
export const getSignedLineNet = (line) => {
  const debit = parseEntryLineAmount(line?.debit);
  const credit = parseEntryLineAmount(line?.credit);
  return debit - credit;
};

/**
 * True when accrual debe/haber should mirror the source (e.g. NC compra credits expense,
 * NC venta debits revenue) instead of the usual invoice spread.
 */
export const shouldInvertAccrualForAccount = (accountKey, sourceEntry) => {
  const key = String(accountKey || '').trim();
  if (!key || !sourceEntry) return false;

  const lines = (sourceEntry.entries || []).filter((l) => String(l?.accountKey || '').trim() === key);
  if (!lines.length) return false;

  const net = lines.reduce((sum, line) => sum + getSignedLineNet(line), 0);
  if (net === 0) return false;

  const isIngreso = isIngresoAccountKey(key);
  // Gasto: invoice Dr expense (net > 0); NC Cr expense (net < 0).
  // Ingreso: invoice Cr revenue (net < 0); NC Dr revenue (net > 0).
  return isIngreso ? net > 0 : net < 0;
};

/**
 * Build two-line accrual entry.
 * Gasto (normal): Dr resultado / Cr balance.
 * Ingreso (normal): Dr balance / Cr resultado.
 * When invert=true, patterns swap (NC / reversed P&L side).
 */
export const buildAccrualLines = (
  amount,
  resultadoAccount,
  balanceAccount,
  pendingLabel = 'Pendiente',
  invert = false,
) => {
  const accountDisplayName = (account) => {
    if (account?.key) return account.name || account.key;
    return pendingLabel;
  };

  const useIngresoPattern = isIngresoAccountKey(resultadoAccount?.key) ? !invert : invert;

  if (useIngresoPattern) {
    return [
      {
        ranking: 1,
        accountKey: balanceAccount?.key || null,
        accountName: accountDisplayName(balanceAccount),
        debit: amount,
        credit: 0,
      },
      {
        ranking: 2,
        accountKey: resultadoAccount?.key || null,
        accountName: accountDisplayName(resultadoAccount),
        debit: 0,
        credit: amount,
      },
    ];
  }

  return [
    {
      ranking: 1,
      accountKey: resultadoAccount?.key || null,
      accountName: accountDisplayName(resultadoAccount),
      debit: amount,
      credit: 0,
    },
    {
      ranking: 2,
      accountKey: balanceAccount?.key || null,
      accountName: accountDisplayName(balanceAccount),
      debit: 0,
      credit: amount,
    },
  ];
};
